/**
 * Snippet insertion — dragging a snippet onto the canvas
 * (docs/design/SNIPPETS-SPEC.md "Library panel").
 *
 * Chords are key-relative, so inserting into a different key/mode than the
 * snippet was captured in is a relabel, not pitch math: scaleDegree,
 * quality, and extensions carry over unchanged; only key/mode and voicing
 * change. Captured voices are discarded here — the revoice pass (shared with
 * the moves engine's integration layer) regenerates them against whatever
 * precedes the drop point in the CURRENT progression.
 */
import { v4 as uuidv4 } from 'uuid';
import type { Chord, MusicalKey, Mode } from '@/types/chord';
import type { Snippet } from '@/types/snippet';
import type { MoveResult } from '@/lib/moves';
import { revoiceMoveResult } from './apply-move';
import { CANVAS_CONFIG } from './constants';

/**
 * dataTransfer MIME type SnippetsPanel writes on drag start and
 * DroppableCanvas reads on drop. Native HTML5 drag-and-drop (not @dnd-kit) —
 * the panel lives outside DroppableCanvas's DndContext tree, so a shared
 * dnd-kit drag session isn't available across the two components.
 */
export const SNIPPET_DRAG_MIME = 'application/x-neume-snippet';

// Matches the size App.tsx uses for every other chord-creation path
// (handleAddChord, buildGhostChord, createDemoChords) — CANVAS_CONFIG's
// DEFAULT_CHORD_SIZE (60) is a different constant used for ghost sizing.
const INSERTED_CHORD_SIZE = 80;

/**
 * Insert a snippet's chords into the current progression at `dropBeat`,
 * transposed into `currentKey`/`currentMode` and revoiced from whatever
 * precedes the insertion point. Returns the full, revoiced chord array
 * (existing chords + inserted ones) — pure, does not mutate its inputs.
 */
export function insertSnippet(
  existingChords: Chord[],
  snippet: Snippet,
  dropBeat: number,
  currentKey: MusicalKey,
  currentMode: Mode,
  zoom: number
): Chord[] {
  const now = new Date().toISOString();

  const inserted: Chord[] = snippet.chords.map((sc) => ({
    id: uuidv4(),
    scaleDegree: sc.scaleDegree,
    quality: sc.quality,
    extensions: sc.extensions,
    key: currentKey,
    mode: currentMode,
    isChromatic: sc.isChromatic ?? false,
    chromaticType: sc.chromaticType,
    // Placeholder — revoiceMoveResult below regenerates every inserted
    // chord's voices from the current progression's voice leading.
    voices: { soprano: 'C4', alto: 'G3', tenor: 'E3', bass: 'C3' },
    startBeat: Math.max(0, dropBeat + sc.startBeat),
    duration: sc.duration,
    position: { x: 0, y: 0 },
    size: INSERTED_CHORD_SIZE,
    selected: false,
    playing: false,
    source: 'library',
    createdAt: now,
  }));

  const fakeResult: MoveResult = {
    chords: [...existingChords, ...inserted],
    changedIds: inserted.map((c) => c.id),
    explanation: '',
    theoryLabel: '',
  };

  return revoiceMoveResult(fakeResult, zoom);
}

/** Beat position under a client X coordinate, snapped to the grid. */
export function beatFromDropX(dropX: number, zoom: number): number {
  const beatWidth = CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom;
  return Math.max(0, Math.round(dropX / beatWidth));
}
